import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { findPost, JOURNAL_POSTS } from "../data/journal";

const JournalPost = () => {
  const { slug } = useParams();
  const post = findPost(slug);
  const cart = useCart();
  const [product, setProduct] = useState(null);

  useEffect(() => {
    if (!post?.product) {
      setProduct(null);
      return;
    }
    api
      .get("/products", { params: { search: post.product } })
      .then((r) => {
        const list = r.data || [];
        setProduct(list.find((p) => p.name.toLowerCase() === post.product.toLowerCase()) || list[0] || null);
      })
      .catch(() => setProduct(null));
  }, [post]);

  if (!post) return <Navigate to="/journal" replace />;

  const others = JOURNAL_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div data-testid="journal-post-page" className="min-h-screen">
      <Header />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10">
        <Link
          data-testid="journal-back"
          to="/journal"
          className="inline-flex items-center gap-2 text-[11px] tracking-overline uppercase text-[#B0A496] hover:text-[#F4EAD9] font-bold"
        >
          <ArrowLeft size={14} strokeWidth={1.2} /> All letters
        </Link>
      </section>

      <article className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 pb-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <header className="md:col-span-12">
          <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-5 font-bold">
            {post.eyebrow} · {post.date}
          </p>
          <h1
            data-testid="journal-post-title"
            className="font-serif-display font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight text-[#F4EAD9] max-w-4xl"
          >
            {post.title}
          </h1>
        </header>

        <div className="md:col-span-7 space-y-7">
          {post.body.map((para, i) => (
            <p
              key={i}
              className={`leading-relaxed text-[#B0A496] ${i === 0 ? "text-lg md:text-xl text-[#F4EAD9]/90" : "text-base md:text-lg"}`}
            >
              {para}
            </p>
          ))}
          <p className="pt-6 text-[11px] tracking-overline uppercase text-[#D4AF37] font-bold">
            Your scent, your story. — Ambdrop
          </p>
        </div>

        <aside className="md:col-span-5 md:col-start-8 self-start md:sticky md:top-24">
          {product ? (
            <div data-testid="journal-featured-product" className="border border-[#2D2622] bg-[#0F0C0A]">
              <Link to={`/product/${product.id}`} className="block aspect-[4/5] overflow-hidden bg-[#1F1A17] image-zoom-parent">
                <img src={product.image_url} alt={product.name} className="image-zoom w-full h-full object-cover" />
              </Link>
              <div className="p-6">
                <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-2 font-bold">Featured in this letter</p>
                <h3 className="font-serif-display text-3xl text-[#F4EAD9] leading-tight">{product.name}</h3>
                <p className="mt-1 text-sm text-[#D4AF37]">{product.notes}</p>
                <p className="mt-4 text-xl tabular-nums text-[#F4EAD9] font-medium">{formatPrice(product.price_inr)}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    data-testid="journal-add-to-cart"
                    onClick={() => {
                      cart.add(product, 1);
                      cart.setOpen(true);
                      toast.success("Added to cart", { description: product.name });
                    }}
                    className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-6 py-3 text-[11px] tracking-overline uppercase font-bold transition-colors"
                  >
                    <ShoppingBag size={14} strokeWidth={1.2} /> Add to cart
                  </button>
                  <Link
                    data-testid="journal-view-product"
                    to={`/product/${product.id}`}
                    className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#15110F] px-6 py-3 text-[11px] tracking-overline uppercase font-bold transition-colors"
                  >
                    View <ArrowUpRight size={14} strokeWidth={1.2} />
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-[#2D2622] bg-[#0F0C0A] p-8">
              <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-3 font-bold">Explore the catalog</p>
              <p className="font-serif-display text-3xl text-[#F4EAD9] leading-tight">
                Find the flacon that <span className="italic text-[#D4AF37]">feels like you</span>.
              </p>
              <Link
                data-testid="journal-shop-cta"
                to="/shop"
                className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-6 py-3 text-[11px] tracking-overline uppercase font-bold transition-colors"
              >
                Shop all <ArrowUpRight size={14} strokeWidth={1.2} />
              </Link>
            </div>
          )}
        </aside>
      </article>

      <section className="border-t border-[#2D2622]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
          <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-8 font-bold">More letters</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {others.map((p) => (
              <Link
                key={p.slug}
                data-testid={`journal-more-${p.slug}`}
                to={`/journal/${p.slug}`}
                className="group border-t border-[#2D2622] pt-5"
              >
                <p className="text-[11px] tracking-overline uppercase text-[#B0A496] font-bold">{p.eyebrow}</p>
                <h3 className="mt-2 font-serif-display text-2xl text-[#F4EAD9] group-hover:text-[#D4AF37] transition-colors leading-tight">
                  {p.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JournalPost;
